/**
 * `advance(state, intents, tickCount) → state` — the deterministic heart.
 *
 * Pure at its boundary: the caller's state is never mutated, and the same
 * (state, intents, tickCount) always produces the same result, on any OS, in
 * any process, in a replay a year from now. Nothing in here reads a clock,
 * touches I/O, or draws from an unnamed source of randomness.
 *
 * The shape of one tick:
 *
 *   1. clock advances to tick `t`
 *   2. intents stamped for `t`, in a canonical order, each parsed → checked → applied
 *   3. `sim` systems
 *   4. `hour` systems, if `t` lands on a game-hour boundary
 *   5. `day` systems, if `t` lands on a game-day boundary
 *
 * Fast-forwarding N ticks runs that loop N times, so it is state-identical to
 * N single steps — a property the tests assert rather than assume, because
 * every offscreen-chunk optimisation later will lean on it.
 */
import { EMPTY_CATALOGUE } from './catalogue.js';
import { remember } from './chronicle.js';
import { pressureSystem } from './scheduler.js';
import { feedDens, PRESSURE_RULES, RAID_SYSTEMS } from './raids.js';
import { BOAR_PRESSURE_RULES } from './boar.js';
import { COMBAT_INTENTS, COMBAT_SYSTEMS } from './combat.js';
import { simEvent } from './event.js';
import { ECOLOGY_SYSTEMS, mindSystem } from './beasts.js';
import { isWellFormedEnvelope, } from './intent.js';
import { CAMP_INTENTS, CAMP_SYSTEMS } from './camp.js';
import { corpseSystem } from './death.js';
import { FORAGE_INTENTS, floraSystem } from './forage.js';
import { INTERACT_INTENTS, interactSystem } from './interact.js';
import { CARRY_INTENTS, burdenSystem } from './carry.js';
import { acheSystem, CAULDRON_INTENTS, houndSystem } from './cauldron.js';
import { VILLAGER_INTENTS, villagerSystem } from './village.js';
import { STALK_INTENTS } from './stalk.js';
import { consentSystem, SURGERY_INTENTS } from './surgery.js';
import { MOVEMENT_INTENTS } from './movement.js';
import { RngRegistry } from './rng.js';
import { SIGN_SYSTEMS } from './tracking.js';
import { SKY_INTENTS, SKY_SYSTEMS } from './sky.js';
import { SPEECH_INTENTS } from './speech.js';
import { WOODCRAFT_INTENTS } from './woodcraft.js';
import { closeErrands } from './errands.js';
import { recordPractice } from './practice.js';
import { TICKS_PER_GAME_DAY, TICKS_PER_GAME_HOUR, gameTime } from './time.js';
import { TICK_PHASES } from './system.js';
import { cloneWorld } from './world.js';
/**
 * Canonical intent order within a tick: by actor, then by submission order.
 *
 * Sorting matters more than it looks. Without it, two players' intents landing
 * in either network order would produce two different worlds — meaning the
 * live server could not be replayed from a recorded script, and two servers
 * fed the same script could disagree. Sorting by `actorId` makes arrival order
 * irrelevant; keeping submission order *within* an actor preserves the one
 * ordering that is genuinely meaningful.
 */
function compareIntents(left, right) {
    if (left.intent.actorId !== right.intent.actorId) {
        return left.intent.actorId < right.intent.actorId ? -1 : 1;
    }
    return left.index - right.index;
}
/** Build a sim bound to a rule set. */
export function createSim(rules) {
    const catalogue = rules.catalogue ?? EMPTY_CATALOGUE;
    const handlers = new Map();
    for (const handler of rules.intents) {
        if (handlers.has(handler.type)) {
            throw new Error(`duplicate intent handler for '${handler.type}'`);
        }
        handlers.set(handler.type, handler);
    }
    const seen = new Set();
    for (const system of rules.systems) {
        if (seen.has(system.name))
            throw new Error(`duplicate system name '${system.name}'`);
        seen.add(system.name);
    }
    // Bucket systems by phase once, at construction, rather than filtering the
    // list 10 times a second forever.
    const byPhase = new Map(TICK_PHASES.map((phase) => [phase, []]));
    for (const system of rules.systems) {
        const bucket = byPhase.get(system.phase);
        if (!bucket)
            throw new Error(`system '${system.name}' has unknown phase '${system.phase}'`);
        bucket.push(system);
    }
    function advance(state, intents = [], tickCount = 1) {
        if (!Number.isInteger(tickCount) || tickCount < 0) {
            throw new RangeError(`advance needs a non-negative integer tickCount (got ${tickCount})`);
        }
        const world = cloneWorld(state);
        const rejected = [];
        const events = [];
        const registry = new RngRegistry(world.seed, world.rng);
        const rng = (name) => registry.get(name);
        // A world restored from before M5-01 has no chronicle. Filled here rather
        // than left to `hydrateWorld`, because `advance` is also handed states that
        // tests build by hand, and an emitter that could not write its news would
        // fail on a `push` to `undefined` at the first blow of the first fight.
        world.chronicle ??= [];
        // …and one from before M5-02 has no governor. Same argument: `advance` is
        // handed hand-built states, and a scheduler that could not record having
        // fired would fire again every hour for ever.
        world.pressure ??= {};
        // …and one from before RC2-01 carries no errands. Same argument again, and
        // it keeps the seam ready: `closeErrands` (wired into `emit` below) must
        // never `Object.entries` an `undefined` on a hand-built or older world.
        world.errands ??= {};
        // …and one from before RC2-04 has no deadfalls. The reward ladder's verbs
        // read `world.deadfalls`, and a hand-built or older world must present an
        // empty table rather than an `undefined` the first `salvage` walks off.
        world.deadfalls ??= {};
        // The one thing the chronicle cannot read off world state: a cow and a wolf
        // are both `creature`, and only the catalogue knows which of them the
        // village keeps.
        const bystanders = {
            livestock: (id) => {
                const mind = world.minds[id];
                if (!mind)
                    return false;
                return catalogue.instinct?.(mind.instinctId)?.disposition === 'docile';
            },
        };
        // The errand defs, once — `closeErrands` matches every event against the
        // whole set, and building the map per tick (let alone per event) would be
        // the kind of quiet waste the emitter cannot afford ten times a second. A
        // world whose catalogue declares no errands gets an empty map and closes
        // nothing, which is the right degradation.
        const errandDefs = new Map((catalogue.errands?.() ?? []).map((def) => [def.id, def]));
        const firstTick = state.tick + 1;
        const lastTick = state.tick + tickCount;
        // Sort intents into the tick each belongs to, rejecting anything outside
        // the window. `advance` holds no queue of its own — a pure function has
        // nowhere to keep one — so intents for future ticks go back to the caller,
        // which is exactly where the server's pending queue lives anyway.
        const scheduled = new Map();
        intents.forEach((intent, index) => {
            if (!isWellFormedEnvelope(intent)) {
                rejected.push({
                    intent,
                    reason: 'malformed-envelope',
                    detail: 'expected { actorId: string, tick: non-negative integer, type: string }',
                });
                return;
            }
            if (intent.tick < firstTick) {
                rejected.push({
                    intent,
                    reason: 'stale-tick',
                    detail: `tick ${intent.tick} was already simulated (world is at ${state.tick})`,
                });
                return;
            }
            if (intent.tick > lastTick) {
                rejected.push({
                    intent,
                    reason: 'future-tick',
                    detail: `tick ${intent.tick} is beyond this advance (ends at ${lastTick})`,
                });
                return;
            }
            const bucket = scheduled.get(intent.tick);
            if (bucket)
                bucket.push({ intent, index });
            else
                scheduled.set(intent.tick, [{ intent, index }]);
        });
        for (const bucket of scheduled.values())
            bucket.sort(compareIntents);
        for (let tick = firstTick; tick <= lastTick; tick += 1) {
            world.tick = tick;
            const time = gameTime(tick);
            // The tick is stamped here rather than by the emitter: a handler that
            // could name the tick could name the wrong one, and an archived event
            // dated to a tick the world never simulated is worse than no event.
            //
            // It is also where the valley's memory is written (M5-01). Every event in
            // the game passes through this closure, which makes it the one place a
            // rumour can be derived from what actually happened rather than from a
            // rule remembering to file its own news. `remember` keeps a handful and
            // drops the rest; see `chronicle.ts` for what counts as news.
            const emit = (init) => {
                const event = simEvent(tick, init);
                events.push(event);
                remember(world, bystanders, event);
                // …and the dens count their own meals off the same stream (M5-02). A
                // rule that had to remember to feed its own den is a rule that will
                // eventually forget.
                feedDens(world, event);
                // …and the core loop reads the same stream (RC2-01/02/04). This is the
                // shared seam RC2-01 and RC2-02 landed their engines against and left
                // for a mechanic ticket to arm: an errand closes when the ordinary event
                // that fulfils it fires, and a practice tally grows off the same event,
                // so neither an errand nor a skill needs a rule that remembers to grant
                // it. Both hand back beats to put on the stream — an archived
                // `completed`, an `actor` "getting better" line — which re-enter this
                // closure; both return nothing for their *own* event types, so the
                // recursion cannot loop.
                for (const closed of closeErrands(world, errandDefs, event, catalogue))
                    emit(closed);
                for (const beat of recordPractice(world, event))
                    emit(beat);
            };
            const context = (phase) => ({
                world,
                tick,
                time,
                phase,
                catalogue,
                rng,
                emit,
            });
            const due = scheduled.get(tick);
            if (due) {
                const intentContext = context('sim');
                for (const { intent } of due) {
                    const rejection = applyIntent(handlers, intent, intentContext);
                    if (rejection)
                        rejected.push(rejection);
                }
            }
            runPhase(byPhase, 'sim', context);
            if (tick % TICKS_PER_GAME_HOUR === 0)
                runPhase(byPhase, 'hour', context);
            if (tick % TICKS_PER_GAME_DAY === 0)
                runPhase(byPhase, 'day', context);
        }
        return { state: world, rejected, events };
    }
    return { advance };
}
function runPhase(byPhase, phase, context) {
    const systems = byPhase.get(phase);
    if (!systems || systems.length === 0)
        return;
    const ctx = context(phase);
    for (const system of systems)
        system.run(ctx);
}
/** parse → check → apply. Returns a rejection, or `undefined` on success. */
function applyIntent(handlers, intent, ctx) {
    const handler = handlers.get(intent.type);
    if (!handler) {
        return { intent, reason: 'unknown-type', detail: `no handler for '${intent.type}'` };
    }
    const parsed = handler.parse(intent);
    if (parsed === null) {
        return { intent, reason: 'invalid-payload', detail: `'${intent.type}' payload was not usable` };
    }
    const illegal = handler.check?.(parsed, ctx) ?? null;
    if (illegal !== null) {
        return { intent, reason: 'illegal', detail: illegal };
    }
    handler.apply(parsed, ctx);
    return undefined;
}
/**
 * The live rule set: the game, as far as the engine is concerned.
 *
 * M0-04 filled in the first entries — joining, leaving and moving; M0-06 added
 * say and examine; M2-01 the combat exchange and the wound clocks that make it
 * matter. Nothing in `advance` above changed when they did, which is the point
 * of the seam.
 *
 * Note what is *not* here: a catalogue. The rules are the same rules whatever
 * content is loaded, and a host that wants bodies in its world supplies one —
 * see `apps/server`.
 */
export const DEFAULT_RULES = {
    // `interactSystem` after the combat systems on purpose: a blow landing this
    // tick must cancel a channel *before* the channel gets its chance to
    // complete, or a looter and the man interrupting them could both succeed on
    // the same tick depending on nothing but alphabetical order.
    // `corpseSystem` after `interactSystem`, so a channel that completes on the
    // tick its target died still resolves against a body that is still there.
    // Robbing a man who bled out a fraction of a second ago is legal; robbing
    // thin air is a crash waiting for a scenario test to find it.
    // `floraSystem` runs in the `hour` phase, so its position in this list is
    // only its order relative to other hourly systems — and it has none it can
    // conflict with: a plant growing back cannot interact with a den restocking.
    // It does now have one it *orders* against: `browseSystem` (M3-10) arrives
    // with `ECOLOGY_SYSTEMS`, so animals eat before plants grow back. Either way
    // round is defensible and this one is the honest one — a patch stripped this
    // hour starts its regrowth clock from this hour, not from the hour before.
    // `SKY_SYSTEMS` last, and it does not matter that they are: the only one on
    // the `sim` phase carries lights to where their bearers already walked this
    // tick, which has to happen *after* movement rather than before it, and the
    // rest are hourly and daily systems with nothing to race.
    systems: [
        mindSystem,
        ...COMBAT_SYSTEMS,
        interactSystem,
        // M4-05. After the channel loop, so a request answered this tick is not
        // swept away in the same tick it was granted.
        consentSystem,
        // M4-06. After the intents that moved anybody this tick, so a burden is
        // glued to its carrier *after* the carrier has walked, and before the
        // corpse pass — a body carried onto a tile and dying there lies where it
        // was put down.
        burdenSystem,
        corpseSystem,
        // M4-07. Hourly, at night, and only for the marked: nothing to race.
        houndSystem,
        acheSystem,
        // M4-08. After the players have moved and before the ecology: a villager
        // crossing the green is the least urgent thing in the tick.
        villagerSystem,
        // M5-02. The herders' shout, on the sim phase because it is a thing a
        // person does when they see something — it has to be able to fire the tick
        // the wolf comes over the fence rather than on the next hour.
        ...RAID_SYSTEMS,
        ...ECOLOGY_SYSTEMS,
        floraSystem,
        ...SKY_SYSTEMS,
        // M3-08. Hourly, and with nothing to race: sign decaying and a den looking
        // lived-in cannot interact with anything else on the hour.
        ...SIGN_SYSTEMS,
        // `CAMP_SYSTEMS` after the sky's, and it matters for exactly one of them:
        // `needsSystem` writes the hunger band onto the body, and `recoverySystem`
        // reads the `nourishment` it derives. Both are hourly or daily, so the
        // ordering costs nothing on the ticks where nobody is hungry.
        ...CAMP_SYSTEMS,
        // M5-02. The valley's own move, last on the hour: everything it reads —
        // who is alive, what is burning, how thin the herd is — should be this
        // hour's answer rather than last hour's. M5-04's boar escalation rides the
        // same scheduler, one substream per rule, so arming the chief cannot shift
        // the dice the wolves draw.
        pressureSystem([...PRESSURE_RULES, ...BOAR_PRESSURE_RULES]),
    ],
    intents: [
        ...MOVEMENT_INTENTS,
        ...SPEECH_INTENTS,
        ...COMBAT_INTENTS,
        ...INTERACT_INTENTS,
        ...SURGERY_INTENTS,
        ...CARRY_INTENTS,
        ...CAULDRON_INTENTS,
        ...VILLAGER_INTENTS,
        ...STALK_INTENTS,
        ...FORAGE_INTENTS,
        ...SKY_INTENTS,
        ...CAMP_INTENTS,
        ...WOODCRAFT_INTENTS,
    ],
};
const defaultSim = createSim(DEFAULT_RULES);
/**
 * Advance the world by `tickCount` ticks under the default rules, with no
 * content loaded.
 *
 * Anything that needs bodies — which is to say anything that fights — should
 * build its own sim with `createSim({ ...DEFAULT_RULES, catalogue })`.
 */
export const advance = (state, intents, tickCount) => defaultSim.advance(state, intents, tickCount);
//# sourceMappingURL=advance.js.map